import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth0, Auth0Provider as Auth0ProviderSDK } from '@auth0/auth0-react';
import { User } from '@/types/user';
import { useToast } from './ToastContext';
import { auth0Config } from '@/config/auth0';
import axios from 'axios';
import { auth0ApiClient } from '@/services/auth0ApiClient';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (redirectUri?: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshUser: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Inner Auth Provider that uses Auth0 SDK hooks
 * This must be wrapped by Auth0ProviderSDK
 */
const InnerAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user: auth0User,
    isLoading: auth0Loading,
    isAuthenticated: auth0Authenticated,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const errorShownRef = useRef(false);
  const syncInProgressRef = useRef(false);
  const lastSyncAuthStateRef = useRef<string | null>(null);

  // Initialize API client with token getter
  useEffect(() => {
    const tokenGetter = async () => {
      try {
        return await getAccessTokenSilently({
          authorizationParams: {
            audience: auth0Config.audience,
          },
        });
      } catch (error) {
        console.error('Failed to get access token:', error);
        throw error;
      }
    };

    auth0ApiClient.setTokenGetter(tokenGetter);
  }, [getAccessTokenSilently]);

  // Sync Auth0 user to local user state and backend
  useEffect(() => {
    // Create a unique key for the current auth state to prevent duplicate syncs
    const authStateKey = auth0Authenticated && auth0User ? `${auth0User.sub}` : 'logged-out';

    // Skip if already syncing or if auth state hasn't changed
    if (syncInProgressRef.current || lastSyncAuthStateRef.current === authStateKey) {
      if (!auth0Loading && lastSyncAuthStateRef.current === authStateKey) {
        setIsLoading(false);
      }
      return;
    }

    const syncUser = async () => {
      // Double-check to prevent race conditions
      if (syncInProgressRef.current) {
        return;
      }

      syncInProgressRef.current = true;
      lastSyncAuthStateRef.current = authStateKey;

      if (auth0Authenticated && auth0User) {
        try {
          // Get Auth0 access token
          const accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: auth0Config.audience,
            },
          });

          setToken(accessToken);

          // Fetch full user profile from backend
          const response = await axios.get(
            `${import.meta.env.VITE_API_BASE_URL || '/api'}/auth/me`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (response.data.success && response.data.user) {
            setUser(response.data.user);
            // Reset error flag on successful load
            errorShownRef.current = false;
          } else {
            console.error('Failed to fetch user profile from backend');
            setUser(null);
          }
        } catch (error) {
          console.error('Error syncing user:', error);

          // Only show error toast once per authentication session
          if (!errorShownRef.current) {
            // If user not found in backend (USER_NOT_SYNCED), show message
            if (axios.isAxiosError(error) && error.response?.data?.code === 'USER_NOT_SYNCED') {
              showToast({
                type: 'error',
                message: 'User profile not synchronized. Please contact support.',
              });
            } else {
              showToast({
                type: 'error',
                message: 'Failed to load user profile',
              });
            }
            errorShownRef.current = true;
          }

          setUser(null);
          setToken(null);
        }
      } else {
        setUser(null);
        setToken(null);
        // Reset error flag when user logs out
        errorShownRef.current = false;
      }

      setIsLoading(false);
      syncInProgressRef.current = false;
    };

    if (!auth0Loading) {
      syncUser();
    }
  }, [auth0Authenticated, auth0User, auth0Loading, getAccessTokenSilently, showToast]);

  const login = async (redirectUri?: string) => {
    try {
      await loginWithRedirect({
        authorizationParams: {
          redirect_uri: redirectUri || auth0Config.redirectUri,
          audience: auth0Config.audience,
          scope: auth0Config.scope,
        },
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Login failed. Please try again.',
      });
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    auth0Logout({
      logoutParams: {
        returnTo: auth0Config.logoutUri,
      },
    });

    showToast({
      type: 'info',
      message: 'You have been logged out.',
    });
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const getAccessToken = async (): Promise<string> => {
    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
        },
      });
      setToken(accessToken);
      return accessToken;
    } catch (error) {
      console.error('Failed to get access token:', error);
      throw error;
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
        },
      });

      // Fetch updated user profile from backend
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL || '/api'}/auth/me`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success && response.data.user) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading: auth0Loading || isLoading,
    isAuthenticated: auth0Authenticated && !!user,
    login,
    logout,
    updateUser,
    refreshUser,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Auth Provider with Auth0 SDK wrapper
 * This is the main export to be used in main.tsx
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <Auth0ProviderSDK
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </Auth0ProviderSDK>
  );
};
